// project imports
import {
  BulkApproveRemediationTrackerTaskDTO,
  BulkRejectRemediationTrackerTaskDTO,
  CheckApprovalDTO,
  ParentChildApprovalDTO,
  RemediationTrackerTaskSearchParameter,
} from "../../../common/types/common.dto.types";
import { ActionResponseType } from "../../../common/types/common.types";
import { ApiClient } from "../../../core/api/apiClient.service";
import {
  MergeRemediationRequestDTO,
  RemediationTrackerTaskQuestionsRequestDTO,
} from "./RemediationTrackerTasksPage.types";

// ==============================|| VIEWS - PAGES - REMEDIATION TRACKER TASKS - API ||============================== //

// client object for axios calls to backend
const client = new ApiClient();

export const getRemediationTrackerTaskInitialData =
  async (): Promise<ActionResponseType> => {
    try {
      const URL: string = `RemediationTrackerTasks/GetRemediationTrackerTaskInitialData`;
      let data = {} as ActionResponseType;
      await client
        .get<ActionResponseType>(URL)
        .then((response: ActionResponseType) => {
          data = response;
        })
        .catch((error) => {
          data = {} as ActionResponseType;
        });

      return data;
    } catch (error) {
      throw error;
    }
  };

export const getRemediationTrackerTaskListApi = async (
  searchParameter: RemediationTrackerTaskSearchParameter
): Promise<ActionResponseType> => {
  try {
    const URL: string = `RemediationTrackerTasks/GetRemediationTrackerTasks`;
    let data = {} as ActionResponseType;
    await client
      .post<ActionResponseType>(URL, searchParameter)
      .then((response: ActionResponseType) => {
        data = response;
      })
      .catch((error) => {
        data = {} as ActionResponseType;
      });

    return data;
  } catch (error) {
    throw error;
  }
};

export const getRemediationTrackerTaskDetails = async (
  searchParameter: RemediationTrackerTaskSearchParameter
): Promise<ActionResponseType> => {
  try {
    const URL: string = `RemediationTrackerTasks/GetRemediationTrackerTaskDetails`;
    let data = {} as ActionResponseType;
    await client
      .post<ActionResponseType>(URL, searchParameter)
      .then((response: ActionResponseType) => {
        data = response;
      })
      .catch((error) => {
        data = { IsSuccess: false } as ActionResponseType;
      });

    return data;
  } catch (error) {
    throw error;
  }
};

export const deleteRemediationTrackerTask = async (
  id: number
): Promise<ActionResponseType> => {
  try {
    const URL: string = `RemediationTrackerTasks/DeleteRemediationTrackerTask/${id}`;
    let data = {} as ActionResponseType;
    await client
      .post<ActionResponseType>(URL, id)
      .then((response: ActionResponseType) => {
        data = response;
      })
      .catch((error) => {
        data = {} as ActionResponseType;
      });

    return data;
  } catch (error) {
    throw error;
  }
};

export const getRemediationTrackerTaskStatisticsApi = async (
  searchParameter: RemediationTrackerTaskSearchParameter
): Promise<ActionResponseType> => {
  try {
    const URL: string = `RemediationTrackerTasks/GetRemediationTrackerTaskStatistics`;
    let data = {} as ActionResponseType;
    await client
      .post<ActionResponseType>(URL, searchParameter)
      .then((response: ActionResponseType) => {
        data = response;
      })
      .catch((error) => {
        data = {} as ActionResponseType;
      });

    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Calls the API to merge remediation tracker tasks.
 *
 * @param mergeRemediationRequest - The request payload containing details of tasks to be merged.
 * @returns {Promise<ActionResponseType>} - The action response from the server.
 *
 * Notes:
 * - Uses POST request to "RemediationTrackerTask/MergeRemediationTrackerTasks".
 * - Handles API errors in the catch block.
 */
export const mergeRemediationTasksApi = async (
  mergeRemediationRequest: MergeRemediationRequestDTO
): Promise<ActionResponseType> => {
  try {
    let URL: string = "RemediationTrackerTask/MergeRemediationTrackerTasks";
    let result: ActionResponseType = {} as ActionResponseType;

    await client
      .post<ActionResponseType>(URL, mergeRemediationRequest)
      .then((response) => {
        result = response;
      })
      .catch((error) => {
        return error;
      });
    return result;
  } catch (error) {
    throw error;
  }
};

/**
 * Calls the API to delete a remediation tracker task.
 *
 * @param remediationTrackerTaskId - The ID of the remediation task to be deleted.
 * @returns {Promise<ActionResponseType>} - The action response from the server.
 *
 * Notes:
 * - Uses POST request to "RemediationTrackerTask/DeleteRemediationTrackerTask/{id}".
 * - Passes the taskId both as a URL parameter and request body.
 */
export const deleteRemediationTasksSubtasksApi = async (
  remediationTrackerTaskId: number
): Promise<ActionResponseType> => {
  try {
    const URL: string = `RemediationTrackerTask/DeleteRemediationTrackerTask/${remediationTrackerTaskId}`;
    let result: ActionResponseType = {} as ActionResponseType;

    await client
      .post<ActionResponseType>(URL, remediationTrackerTaskId)
      .then((response) => {
        result = response;
      })
      .catch((error) => {
        return error;
      });
    return result;
  } catch (error) {
    throw error;
  }
};

/**
 * Calls the API to unmerge a previously merged remediation tracker task.
 *
 * @param remediationTrackerTaskId - The ID of the remediation task to unmerge.
 * @returns {Promise<ActionResponseType>} - The action response from the server.
 *
 * Notes:
 * - Uses POST request to "RemediationTrackerTask/UnmergeRemediationTrackerTask/{id}".
 * - Passes the taskId both as a URL parameter and request body.
 */
export const unMergeRemediationTasksApi = async (
  remediationTrackerTaskId: number
): Promise<ActionResponseType> => {
  try {
    const URL: string = `RemediationTrackerTask/UnmergeRemediationTrackerTask/${remediationTrackerTaskId}`;
    let result: ActionResponseType = {} as ActionResponseType;

    await client
      .post<ActionResponseType>(URL, remediationTrackerTaskId)
      .then((response) => {
        result = response;
      })
      .catch((error) => {
        return error;
      });
    return result;
  } catch (error) {
    throw error;
  }
};

export const getRemediationTrackerTaskQuestionDetailsApi = async (
  remediation: RemediationTrackerTaskQuestionsRequestDTO
): Promise<ActionResponseType> => {
  try {
    const URL: string = `RemediationTrackerTasks/GetRemediationTrackerTaskQuestionsDetails`;
    let data = {} as ActionResponseType;
    await client
      .post<ActionResponseType>(URL, remediation)
      .then((response: ActionResponseType) => {
        data = response;
      })
      .catch((error) => {
        data = { IsSuccess: false } as ActionResponseType;
      });

    return data;
  } catch (error) {
    throw error;
  }
};

export const CheckBulkApproveRejectRemediationTrackerTaskApi = async (
  checkApprovalData: CheckApprovalDTO
): Promise<ActionResponseType> => {
  try {
    const URL: string = `RemediationTrackerTasks/GetIdentifiersForApproveOrReject`;
    let data = {} as ActionResponseType;
    await client
      .post<ActionResponseType>(URL, checkApprovalData)
      .then((response: ActionResponseType) => {
        data = response;
      })
      .catch((error) => {
        data = {} as ActionResponseType;
      });

    return data;
  } catch (error) {
    throw error;
  }
};

export const bulkApproveRemediationTrackerTaskApi = async (
  selectedRemediationTrackerTask: BulkApproveRemediationTrackerTaskDTO
): Promise<ActionResponseType> => {
  try {
    const URL: string = `RemediationTrackerTasks/BulkApproveRemediationTrackerTask`;
    let data = {} as ActionResponseType;
    await client
      .post<ActionResponseType>(URL, selectedRemediationTrackerTask)
      .then((response: ActionResponseType) => {
        data = response;
      })
      .catch((error) => {
        data = {} as ActionResponseType;
      });

    return data;
  } catch (error) {
    throw error;
  }
};

export const bulkRejectRemediationTrackerTaskApi = async (
  selectedRemediationTrackerTask: BulkRejectRemediationTrackerTaskDTO
): Promise<ActionResponseType> => {
  try {
    const URL: string = `RemediationTrackerTasks/BulkRejectRemediationTrackerTask`;
    let data = {} as ActionResponseType;
    await client
      .post<ActionResponseType>(URL, selectedRemediationTrackerTask)
      .then((response: ActionResponseType) => {
        data = response;
      })
      .catch((error) => {
        data = {} as ActionResponseType;
      });

    return data;
  } catch (error) {
    throw error;
  }
};
